import type { ElementType } from 'react';
import type { MotionProps } from 'framer-motion';
import type { BoxProps } from '@mui/material/Box';

import { motion as fm } from 'framer-motion';

import Box from '@mui/material/Box';

import { revealContainer, revealItem } from '../../theme/motion';

// ----------------------------------------------------------------------

// Created once at module scope (motion.create per-render would remount children).
const MotionBox = fm.create(Box);

// Built from named BoxProps + MotionProps (framer overrides the conflicting
// DOM/pan handlers, so omit those Box keys) rather than `ComponentProps<typeof
// MotionBox>`, whose inferred type isn't portably nameable in the emitted .d.ts
// (TS2742). `component` is re-declared because the non-polymorphic BoxProps
// default drops Box's polymorphic `component` prop.
type MotionBoxProps = Omit<BoxProps, keyof MotionProps> & MotionProps & { component?: ElementType };

export type StaggerProps = MotionBoxProps;

/**
 * Container for a once-only staggered reveal. Fires on mount (not on scroll)
 * because these lists sit at the top of the page. The container itself has no
 * visual animation — it only orchestrates the children's stagger timing.
 * Decorative — never blocks interaction. Reduced motion is honored globally via
 * `<MotionConfig reducedMotion="user">`, which keeps opacity and drops `y`.
 *
 * Accepts all `Box` props (`sx`, `component`, etc.).
 */
export function Stagger({ children, ...rest }: StaggerProps) {
  return (
    <MotionBox initial="hidden" animate="show" variants={revealContainer} {...rest}>
      {children}
    </MotionBox>
  );
}

export type StaggerItemProps = MotionBoxProps;

/** A single revealed item — fades + rises in. Accepts all `Box` props. */
export function StaggerItem({ children, ...rest }: StaggerItemProps) {
  return (
    <MotionBox variants={revealItem} {...rest}>
      {children}
    </MotionBox>
  );
}
